import type { ReactNode } from "react";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { pickSeries } from "./series";
import type { ReportTrends } from "./types";

/** The chart slots a trends payload fills. Null when the page is a sentence. */
export type InsightSlots = {
  margin: boolean;
  ranked: boolean;
  costs: boolean;
  rules: boolean;
};

/**
 * Which charts this report will draw, from a trends payload already in hand
 * (this period, or an earlier one of the same agent). The declaration is
 * stable across periods, so a week that is still loading keeps those slots
 * instead of a fixed pair of charts.
 */
export const chartSlots = (
  trends: ReportTrends,
  referenced: string[],
): InsightSlots | null => {
  if (trends.periods.length < 2) return null;
  const gap =
    trends.reason_code === "no_rollups" ||
    trends.reason_code === "untracked_rollups";
  if (gap && trends.rules.length === 0) return null;
  const { over, ranked } = pickSeries(trends.datasets, referenced);
  return {
    margin: !!over,
    ranked: !!ranked,
    costs: !!over && over.costs.length > 0,
    rules: trends.rules.length > 0,
  };
};

/** The reading column, one line per insight, beside the chart it is about. */
export const ReadingSkeleton = ({ count }: { count: number }) => (
  <Stack
    gap={3}
    align="stretch"
    className="max-h-96 overflow-hidden"
    aria-hidden
  >
    <Skeleton className="h-4 w-32" />
    {Array.from({ length: count }, (_, line) => (
      <Skeleton key={line} className="h-5 w-full" />
    ))}
  </Stack>
);

const ChartSlot = () => (
  <Stack
    gap={3}
    align="stretch"
    className="min-w-0 rounded-xl border border-line p-4"
    aria-hidden
  >
    <Skeleton className="h-4 w-40" />
    <Skeleton className="h-64 w-full" />
  </Stack>
);

/**
 * The insights body in the shape this report takes. With no payload yet, only
 * the primary chart is held: the second row appears when the series says it
 * exists, rather than two invented charts being replaced.
 */
export const InsightsLayoutSkeleton = ({
  reading,
  charts,
}: {
  reading: ReactNode;
  /** Omitted until a trends payload says which series this report has. */
  charts?: InsightSlots;
}) => {
  const margin = charts?.margin ?? true;
  const ranked = charts?.ranked ?? false;
  const costs = charts?.costs ?? false;
  const rules = charts?.rules ?? false;
  return (
    <Stack gap={8} align="stretch" className="@container w-full">
      {(reading || margin) && (
        <div
          className={cn("grid w-full min-w-0 items-start gap-8", {
            "@3xl:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]":
              !!reading && margin,
          })}
        >
          {reading}
          {margin && <ChartSlot />}
        </div>
      )}
      {(ranked || costs || rules) && (
        <div className="grid w-full min-w-0 grid-cols-1 gap-6 @3xl:grid-cols-2">
          {ranked && <ChartSlot />}
          {costs && <ChartSlot />}
          {rules && (
            <Skeleton className="h-48 w-full rounded-xl @3xl:col-span-2" />
          )}
        </div>
      )}
    </Stack>
  );
};

/** A page that will say why there is nothing to chart, not draw charts. */
export const InsightsSentenceSkeleton = () => (
  <Stack align="center" gap={3} className="py-16" aria-hidden>
    <Skeleton className="size-16 rounded-xl" />
    <Skeleton className="h-5 w-64" />
    <Skeleton className="h-4 w-80 max-w-full" />
  </Stack>
);
